import type { NodeViewRenderer, NodeViewRendererProps } from '@tiptap/core';
import type { Node as PmNode } from '@tiptap/pm/model';
import classNames from 'classnames';

import { LAYOUT_CELL_HANDLE } from './data-hooks';
import styles from './layouts-scss';
import { getHandleSmallIcon } from './shared';

export const cellNodeView: NodeViewRenderer = ({ node, getPos, editor }) => {
  const cellElement = document.createElement('div');

  // Cell handle must be wrapped in a div with contenteditable=false to prevent setting selection inside contentElement
  // on pointerdown
  const cellHandleWrapper = document.createElement('div');
  cellHandleWrapper.setAttribute('contenteditable', 'false');

  const cellHandle = document.createElement('button');
  cellHandle.classList.add(styles.columnHandlerContainer);
  cellHandle.setAttribute('type', 'button');
  cellHandle.innerHTML = getHandleSmallIcon(styles.columnHandler);

  cellHandleWrapper.append(cellHandle);

  const resizeHandle = document.createElement('div');
  resizeHandle.classList.add(styles.resizeHandler);
  resizeHandle.setAttribute('contenteditable', 'false');

  const contentElement = document.createElement('div');
  contentElement.classList.add(styles.cellContent);

  cellElement.append(cellHandleWrapper, contentElement);

  updateCellView({ cellElement, cellHandle, resizeHandle, node, getPos, editor });

  cellHandle.addEventListener('click', event => {
    const cell = (event.target as HTMLElement)?.closest(`[data-layout-cell]`) as HTMLElement;
    const cellId = cell?.getAttribute('data-layout-cell');

    if (cellId) {
      editor.chain().selectCell(cellId).focus().run();
    }
  });

  return {
    dom: cellElement,
    contentDOM: contentElement,
    update: (newNode: PmNode) => {
      if (newNode.type !== node.type) {
        return false;
      }
      // eslint-disable-next-line no-param-reassign
      node = newNode;
      updateCellView({ cellElement, cellHandle, resizeHandle, node: newNode, getPos, editor });
      return true;
    },
  };
};

const updateCellView = ({
  cellElement,
  resizeHandle,
  cellHandle,
  node,
  getPos,
  editor,
}: Pick<NodeViewRendererProps, 'node' | 'getPos' | 'editor'> & {
  cellElement: HTMLDivElement;
  cellHandle: HTMLButtonElement;
  resizeHandle: HTMLDivElement;
}) => {
  const { $to, $from } = editor.state.selection;
  const pos = getPos();
  const isSelected = pos === $from.pos && pos + node.nodeSize === $to.pos;
  const isFirst = !editor.state.doc.resolve(pos).nodeBefore;

  cellElement.setAttribute('data-layout-cell', node.attrs.id);
  cellElement.classList.value = classNames({
    [styles.cell]: true,
    [styles.cellSelected]: isSelected,
  });

  cellHandle.setAttribute('data-layout-cell-handle', node.attrs.id);
  cellHandle.setAttribute('data-hook', `${LAYOUT_CELL_HANDLE}-${node.attrs.id}`);
  resizeHandle.setAttribute('data-layout-cell-resize-handle', node.attrs.id);

  if (isFirst) {
    resizeHandle.remove();
  } else {
    cellElement.append(resizeHandle);
  }
};
